import {
  buildGuide,
  paragraphs,
  bullets,
  steps,
  callout,
  workflow,
  faq,
  table,
  fieldTable,
  permissionTable,
  heading,
} from '../helpers';

const finPerm = permissionTable([
  ['manage_finance', 'Finance screens, payments, cash counter, rates, draft invoice'],
  ['view_reports', 'Reports access (often paired with finance roles)'],
]);

const reportPerm = permissionTable([
  [
    'manage_finance or view_reports',
    'Required to open Finance → Reports Hub and export registers',
  ],
]);

export const financeGuides = [
  buildGuide({
    id: 'finance-overview',
    title: 'Finance Overview',
    moduleId: 'finance',
    summary:
      'Payments, rates, cash counter, invoices, and financial dashboards for the property.',
    keywords: ['finance', 'payments', 'cash', 'rates', 'invoice', 'revenue'],
    pathHint: 'Hotel → Finance',
    related: [
      'finance-dashboard',
      'payments',
      'cash-counter',
      'manage-rate',
      'draft-invoice',
      'reports-hub',
    ],
    overview: [
      ...paragraphs(
        'Finance tools in Roomnexa record how money moves through the property and how selling prices are set. Front office posts payments on bookings, cashiers run Cash Counter sessions, managers maintain daily rates, and accounts exports registers from Reports.',
        'Think of Finance as four jobs: (1) see money KPIs on Finance Dashboard, (2) find and audit payment lines, (3) keep the physical cash drawer accountable with Cash Counter, and (4) keep sell rates current on Manage Rate — plus Draft Invoice when you need a formal PDF outside the normal checkout path.',
        'Configuration comes first. Finance Setup and Invoice Setup (under Configuration) define GSTIN, tax behaviour, and invoice branding. Without them, invoices and GST reports look incomplete.',
        'Restaurant POS has its own payment drawer for F&B. Hotel Finance Payments focuses on lodging-related tenders; consolidated payment exports can cover hotel and/or restaurant scopes from Reports.',
        'Expense-like purchase cost for goods often appears via Stock purchases and performance metrics. There is no separate Finance → Expenses route in the current app — use Stock for goods vendors and purchase documents.'
      ),
      callout(
        'note',
        'Expenses',
        'Goods purchase cost is tracked through Stock purchases/performance metrics — there is no separate Finance → Expenses route in the current app.'
      ),
      table(
        ['Area', 'Use it for'],
        [
          ['Finance Dashboard', 'Revenue, expense signals, payment method mix'],
          ['Payments', 'Searchable register of posted tenders'],
          ['Cash Counter', 'Open/close cashier sessions, withdraw, convert, ledger, print'],
          ['Manage Rate', 'Daily rates by room type and rate plan'],
          ['Draft Invoice', 'Standalone invoice PDF generation'],
          ['Reports', 'Bookings, payments, GST, police log, and more exports'],
        ]
      ),
    ],
    beforeYouBegin: [
      finPerm,
      bullets([
        'Complete Finance Setup + Invoice Setup under Configuration.',
        'Create rate plans and room types before Manage Rate.',
        'Agree cash float and who may open/close Cash Counter.',
        'Train that refunds need supervisor approval — not every cashier.',
      ]),
    ],
    steps: [
      heading('Bring Finance online'),
      steps([
        {
          text: 'Configure Finance Setup (GSTIN/tax) and Invoice Setup (logo, legal lines) under Configuration.',
        },
        {
          text: 'Set Manage Rate for each active rate plan across upcoming dates.',
        },
        {
          text: 'Train cashiers on posting booking payments and on Cash Counter open → operate → close.',
        },
        {
          text: 'Practice finding a payment in Finance → Payments by date and method.',
        },
        {
          text: 'Run Reports Hub exports for payments and GST with your accountant’s preferred date ranges.',
        },
      ]),
    ],
    screens: [
      bullets(
        [
          'Finance Dashboard — KPI cards, charts, date filters, PDF download',
          'Payments — filterable payment lines',
          'Cash Counter — session, withdraw/convert, ledger, close, print/PDF',
          'Manage Rate — calendar/grid pricing',
          'Draft Invoice — invoice editor + PDF',
          'Reports — export hub cards',
        ],
        'Main Finance screens'
      ),
    ],
    workflow: [
      workflow('Money day', [
        'Open cash session',
        'Post booking / POS payments',
        'Handle withdraw/convert if needed',
        'Close cash session',
        'Run payment / GST reports',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Payments reduce folio balances and create auditable money events. Cash Counter tracks physical drawer accountability for a shift — expected cash vs counted cash. Manage Rate drives future quotes; historical folios keep amounts already posted unless a booking is amended.',
        'Draft invoices pull hotel invoice setup and booking/charge context where linked. GST reports depend on invoices actually generated from billing/checkout flows.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Close cash daily',
        'Even card-heavy desks benefit from a clean session close — it forces a daily money review.'
      ),
      callout(
        'warning',
        'Never share cashier login',
        'Cash Counter accountability collapses if two people share one open session user.'
      ),
      callout(
        'note',
        'Goods vendors',
        'Stock → Suppliers is where purchase vendors live — not Customer Hub.'
      ),
    ],
    bestPractices: [
      bullets([
        'Close Cash Counter every shift; do not leave sessions open overnight.',
        'Match UPI/bank references in payment notes for easy reconciliation.',
        'Refund only with written supervisor approval.',
        'Keep a floor rate documented and never leave future Manage Rate cells at zero.',
        'Standardise report date ranges with accounts (usually calendar month).',
        'Clear open POS room charges before checkout to avoid finance surprises.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Posting a payment to the wrong booking.',
        'Closing cash while POS bills or petty withdrawals are unexplained.',
        'Changing rates after quoting without telling the desk.',
        'Generating tax invoices before charges are final.',
        'Assuming Stock purchases appear under a Finance Expenses menu.',
        'Emailing GST/police exports without access control.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Cannot open Finance',
            'Missing manage_finance / package',
            'Ask admin to grant permission and confirm package',
          ],
          [
            'Invoice missing logo/GSTIN',
            'Invoice/Finance Setup incomplete',
            'Complete Configuration setups first',
          ],
          [
            'Quote shows zero rate',
            'Manage Rate empty for plan/type/date',
            'Fill Manage Rate and re-quote',
          ],
          [
            'Payment not found',
            'Wrong filters / posted on POS scope',
            'Widen dates; check booking folio and Reports scopes',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Where are vendors?',
          a: 'Goods suppliers live under Stock → Suppliers. Guest customers live under Customer Hub.',
        },
        {
          q: 'Is Cash Counter mandatory?',
          a: 'Strongly recommended for cash-heavy properties. Card-only desks still benefit from shift discipline.',
        },
        {
          q: 'Do restaurant payments show here?',
          a: 'Restaurant has its own payment flows; Finance Payments focuses on hotel tenders. Use Reports → Payments for hotel and/or restaurant registers.',
        },
        {
          q: 'What should new cashiers learn first?',
          a: 'Posting a booking payment correctly, then Cash Counter open/close, then finding lines in Payments.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'finance-dashboard',
    title: 'Finance Dashboard',
    moduleId: 'finance',
    summary: 'Revenue, expense, and payment overview KPIs for managers.',
    keywords: [
      'finance dashboard',
      'revenue kpi',
      'payment methods',
      'finance analytics',
    ],
    pathHint: 'Finance Dashboard',
    related: ['payments', 'performance-dashboard', 'reports-payments'],
    overview: [
      ...paragraphs(
        'Finance Dashboard summarises money metrics for the property at a glance: revenue, expense signals (including restaurant expense where returned), advances, and payment method mix across a date range.',
        'Managers use it to prepare owner packs, spot refund spikes, and see whether collections arrived before or after check-in when payment-timing analytics are available.',
        'It overlaps with Performance Dashboard but stays money-forward. Use Finance when the question is “what came in and how?”, and Performance when the question is broader hotel KPI health.',
        'Charts and tables are only as good as posted payments and related financial events. Empty ranges usually mean no postings — not that the hotel had zero activity on paper.'
      ),
    ],
    beforeYouBegin: [
      finPerm,
      bullets([
        'Pick a meaningful date range (yesterday, MTD, last week).',
        'Know whether restaurant expense signals are relevant for your package.',
      ]),
    ],
    steps: [
      steps([
        { text: 'Open Finance Dashboard.' },
        { text: 'Set start and end dates for the review window.' },
        {
          text: 'Read KPI cards for revenue, expenses, and related totals.',
        },
        {
          text: 'Review payment method breakdown and daily charts.',
        },
        {
          text: 'Drill into Payments or Reports when a number needs evidence.',
        },
        {
          text: 'Download PDF when preparing an owner or accounts pack.',
        },
      ]),
    ],
    screens: [
      bullets([
        'Date range header controls',
        'KPI metric cards (revenue, expenses, advances as provided)',
        'Daily charts for revenue/expense patterns',
        'Payment method breakdown visuals',
        'Payment timing analytics when available',
        'PDF download',
      ]),
      table(
        ['Signal', 'Investigate with'],
        [
          ['Revenue spike/drop', 'Bookings report + Payments report'],
          ['Refund-looking dips', 'Payments list filtered by date/method'],
          ['Method imbalance', 'Pivot Payments report by UPI/card/cash'],
        ]
      ),
    ],
    workflow: [
      workflow('Owner pack prep', [
        'Open Finance Dashboard',
        'Set MTD range',
        'Note variances',
        'Export supporting reports',
        'Annotate for owner',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'The dashboard aggregates posted financial events in the selected range, including payment lists and analytics payloads from the finance API.',
        'Expense figures can include hotel and restaurant expense signals derived from how the property posts costs — they are not a full general ledger.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Same window weekly',
        'Compare like-for-like weekdays (Mon–Sun vs Mon–Sun) when explaining swings to owners.'
      ),
      callout(
        'tip',
        'Advances matter',
        'Future advance totals help you see cash collected for arrivals not yet in-house.'
      ),
      callout(
        'note',
        'PDF for meetings',
        'Download the dashboard PDF before weekly owner calls so everyone sees the same snapshot.'
      ),
    ],
    bestPractices: [
      bullets([
        'Investigate sudden refund or expense spikes the same day.',
        'Reconcile dashboard revenue to Payments report weekly.',
        'Label hotel vs restaurant commentary when presenting mixed figures.',
        'Keep a short variance note (group arrival, channel promo) with each pack.',
        'Do not change rates mid-meeting based on a single noisy day — check trend.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Mixing hotel and restaurant totals without labelling.',
        'Using a partial month range and calling it “monthly revenue”.',
        'Trusting zeros without checking permissions or postings.',
        'Ignoring payment method shifts that signal collection problems.',
        'Confusing Finance Dashboard with Stock valuation.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'All zeros',
            'No payments / wrong dates / permissions',
            'Confirm postings; widen range; check role',
          ],
          [
            'PDF incomplete',
            'Still loading / browser block',
            'Wait for data; allow downloads; retry',
          ],
          [
            'Disagrees with Payments list',
            'Different filters/scopes',
            'Align dates; include advances/refunds consciously',
          ],
          [
            'Expense looks wrong',
            'Stock/restaurant cost timing',
            'Review Stock Ins and F&B posting SOP with accounts',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'vs Performance Dashboard?',
          a: 'Overlapping analytics. Finance is money-forward; Performance is broader hotel KPIs (occupancy, etc.).',
        },
        {
          q: 'Does it include POS?',
          a: 'Restaurant-related expense/payment signals may appear where the API returns them; detailed F&B registers are also in Reports → Restaurant / Payments.',
        },
        {
          q: 'Can front desk use this?',
          a: 'Usually managers. Cashiers live in Payments and Cash Counter day to day.',
        },
        {
          q: 'Is this accounting software?',
          a: 'No — operational finance KPIs and registers for the hotel. Your CA still runs formal books/GST filing.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'payments',
    title: 'Payment Management',
    moduleId: 'finance',
    summary:
      'View and manage payment lines across bookings and related settlements.',
    keywords: [
      'payments',
      'payment list',
      'advance',
      'refund',
      'settlement',
    ],
    pathHint: 'Finance → Payments',
    related: ['check-out', 'cash-counter', 'pos-payments', 'reports-payments'],
    overview: [
      ...paragraphs(
        'Payments is the searchable register of recorded tenders — advances, settlements, and related payment lines — so cashiers and auditors can find money history without opening every booking one by one.',
        'Most payments are created from booking/checkout flows (and POS for restaurant). This screen is primarily for verification, dispute tracing, and daily reconciliation support.',
        'Filters by date, method, and booking context help you answer “did the UPI land?” and “which folio was this advance on?” quickly during peak hours.',
        'Refunds and corrections must follow your property authorisation SOP. Duplicate posts are a common cashier error when a network blip makes staff click twice — always verify before posting again.'
      ),
    ],
    beforeYouBegin: [
      finPerm,
      bullets([
        'Know how your property records advances vs checkout settlements.',
        'Have bank/UPI settlement references available for reconciliation days.',
      ]),
    ],
    steps: [
      steps([
        { text: 'Open Finance → Payments.' },
        {
          text: 'Filter by date range, payment method, and booking/guest identifiers as available.',
        },
        {
          text: 'Open a payment line to verify amount, method, and reference notes.',
        },
        {
          text: 'Jump to the related booking folio when the dispute is about the stay balance.',
        },
        {
          text: 'Handle refunds only through authorised refund flows with written approval.',
        },
        {
          text: 'For day-end, export Reports → Payments and match to Cash Counter close.',
        },
      ]),
    ],
    screens: [
      bullets([
        'Payments table',
        'Date / method / booking filters',
        'Row details',
        'Links into booking context where provided',
      ]),
      fieldTable([
        ['Date filters', 'Recommended', 'Limit to shift or business day'],
        ['Payment method', 'Optional', 'Cash, UPI, card, etc.'],
        ['Booking / guest search', 'Optional', 'Trace a specific stay'],
        ['Amount / reference', '—', 'What was collected and any bank ref notes'],
      ]),
    ],
    workflow: [
      workflow('Trace a payment', [
        'Search guest or date',
        'Open payment line',
        'Open booking folio',
        'Confirm method & reference',
        'Resolve with guest/accounts',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Each payment line is an auditable money event linked to its source document (typically a booking). Advances appear here and on the folio; they reduce what is due at checkout.',
        'Restaurant payments may live in POS payment views; some consolidated exports appear under Reports → Payments when you choose restaurant scope.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Advances',
        'Take advances on reservation when policy allows — they show here and protect no-show risk.'
      ),
      callout(
        'tip',
        'UPI references',
        'Paste UTR/reference into notes at posting time so evening recon takes minutes, not hours.'
      ),
      callout(
        'warning',
        'Duplicate clicks',
        'If the screen hangs, check Payments/folio before posting a second time.'
      ),
    ],
    bestPractices: [
      bullets([
        'Match bank UPI references in notes every time.',
        'Refund only with written approval and reason.',
        'Reconcile cash payments to Cash Counter the same day.',
        'Teach new cashiers to open the folio after posting to confirm balance.',
        'Separate training posts on a demo hotel — never practice refunds on live guests.',
        'Escalate mismatched card batch totals immediately to the manager.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Duplicate payment posts after a slow save.',
        'Posting to the wrong booking with a similar guest name.',
        'Forgetting to record advances that were taken on WhatsApp offline.',
        'Refunding without updating the guest-facing explanation.',
        'Mixing restaurant cash into hotel payment totals without labelling.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Missing payment',
            'Narrow filters / wrong date / POS scope',
            'Widen filters; check booking folio; check restaurant payments',
          ],
          [
            'Amount mismatch vs bank',
            'Wrong method or duplicate',
            'Compare timestamps; review folio history',
          ],
          [
            'Cannot refund',
            'Permission / SOP block',
            'Ask supervisor; use authorised refund flow',
          ],
          [
            'Guest says paid, folio open',
            'Never posted / posted elsewhere',
            'Search Payments by mobile/date; post if verified',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Restaurant payments too?',
          a: 'Restaurant has its own payment drawer; consolidated views appear in Reports → Payments when you select restaurant scope.',
        },
        {
          q: 'Where do I post a payment?',
          a: 'Usually from the booking/checkout payment UI. This list is the register of what was posted.',
        },
        {
          q: 'Are refunds included?',
          a: 'Refund lines appear when posted as payment events. Confirm with your refund SOP.',
        },
        {
          q: 'Difference from Payments Report?',
          a: 'This screen is interactive operations UI; Reports → Payments is export-oriented for accounts.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'cash-counter',
    title: 'Cash Counter',
    moduleId: 'finance',
    summary:
      'Open/close cashier sessions, withdraw, convert, ledger, and print shift accountability.',
    keywords: [
      'cash counter',
      'cashier',
      'cash session',
      'drawer',
      'petty cash',
      'float',
    ],
    pathHint: 'Finance → Cash Counter',
    related: ['payments', 'pos-payments', 'reports-payments'],
    overview: [
      ...paragraphs(
        'Cash Counter is the float and session manager for front-office cash accountability. Open a session at shift start with an opening balance, operate through the shift, then close with a physical count.',
        'During the session you can record withdrawals (cash leaving the drawer for legitimate reasons) and conversions as your process requires. The ledger and timeline show what happened in the session.',
        'Hotel and restaurant cash collection breakdowns help managers see where drawer cash originated. Expected cash on close is compared to what you count — variances need explanation, not silence.',
        'Print or download session PDFs for manager sign-off. This is your night-audit style evidence that the drawer was controlled.',
        'One cashier owner per open session. Shared logins destroy the audit trail and make variances impossible to assign.'
      ),
    ],
    beforeYouBegin: [
      finPerm,
      bullets([
        'Know the opening float amount and denomination mix if required.',
        'One cashier owner per session — no shared passwords.',
        'Clear how POS cash drops are handed to the front-office drawer.',
      ]),
    ],
    steps: [
      heading('Run a cash shift'),
      steps([
        { text: 'Open Finance → Cash Counter.' },
        {
          text: 'Open a new session with the correct opening balance (use suggested opening when it matches the previous close).',
        },
        {
          text: 'During the shift, post booking cash payments as usual; record Withdraw when cash leaves for petty/legitimate reasons.',
        },
        {
          text: 'Use Convert when your property process requires converting tender types inside the session.',
        },
        {
          text: 'Review ledger / timeline entries if a variance is brewing mid-shift.',
        },
        {
          text: 'Count cash blind if policy requires; enter counted amount and close the session.',
        },
        {
          text: 'Print/download the session PDF and hand variance notes to the manager.',
        },
      ]),
    ],
    screens: [
      bullets([
        'Open session controls and opening balance entry',
        'Summary cards for session status',
        'Cash collection breakdown (hotel / restaurant context)',
        'Withdraw / Convert actions',
        'Ledger and timeline',
        'Close session count entry',
        'Print / PDF download',
      ]),
      fieldTable([
        ['Opening balance', 'Yes', 'Float you start the shift with'],
        ['Withdraw amount / reason', 'When used', 'Cash removed from drawer'],
        ['Convert', 'When used', 'Tender conversion inside session per SOP'],
        ['Counted cash on close', 'Yes', 'Physical count at shift end'],
        ['Ledger entry edit/delete', 'Supervised', 'Corrections only under control'],
      ]),
    ],
    workflow: [
      workflow('Shift cash', [
        'Open session',
        'Collect payments',
        'Record withdraw/convert',
        'Count & close',
        'Print PDF',
        'Manager sign-off',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Sessions isolate accountability by cashier and time window. Expected cash is derived from opening balance plus cash collections minus withdrawals/conversions per session rules.',
        'Variances between expected and counted cash must be explained. Editing or deleting ledger entries should be rare and supervised — they change the audit story.'
      ),
    ],
    tips: [
      callout(
        'warning',
        'Never leave open sessions overnight',
        'Close even if the next shift continues — the next cashier opens a new session.'
      ),
      callout(
        'tip',
        'Blind count',
        'Count without looking at expected cash first when your SOP requires blind closes.'
      ),
      callout(
        'tip',
        'POS drops',
        'Record restaurant cash drops clearly so hotel vs restaurant breakdown stays honest.'
      ),
      callout(
        'important',
        'Petty cash',
        'If cash leaves for supplies, post Withdraw with a real reason — do not silently pocket from the float.'
      ),
    ],
    bestPractices: [
      bullets([
        'Blind count when policy allows.',
        'Separate POS cash drops with clear notes.',
        'Manager reviews every variance above a set threshold.',
        'Print/PDF archive daily with night pack.',
        'Train relief cashiers before festivals — mistakes spike on busy nights.',
        'Never “fix” a shortage by editing ledger without investigation.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Two people using one open session.',
        'Forgetting withdrawals for petty expenses.',
        'Closing without counting (typing expected amount).',
        'Leaving session open across midnight casually.',
        'Mixing personal change with hotel float.',
        'Skipping PDF printout so morning manager has no evidence.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Cannot close',
            'Missing count fields / validation',
            'Complete required counted cash fields',
          ],
          [
            'Wrong expected cash',
            'Missed withdraw or payment posts',
            'Review ledger + Payments for the shift',
          ],
          [
            'Cannot open new session',
            'Previous session still open',
            'Close the open session first',
          ],
          [
            'PDF looks wrong',
            'Session still mid-edit',
            'Close/refresh; regenerate print',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Is Cash Counter mandatory?',
          a: 'Strongly recommended for cash-heavy properties; card-only desks still benefit from shift discipline and PDF archives.',
        },
        {
          q: 'Does it post booking payments?',
          a: 'Payments are posted on bookings/POS. Cash Counter accounts for cash that should be in the drawer for the session.',
        },
        {
          q: 'Can I edit ledger lines?',
          a: 'Supervised edit/delete exists for corrections — use sparingly and document why.',
        },
        {
          q: 'Hotel vs restaurant cash?',
          a: 'Collection breakdown shows sources when available; keep drops labelled so managers can read the split.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'manage-rate',
    title: 'Manage Rate (Daily Rates)',
    moduleId: 'finance',
    summary:
      'Set daily rates by room type and rate plan on a calendar grid.',
    keywords: [
      'manage rate',
      'daily rate',
      'tariff',
      'pricing grid',
      'BAR',
    ],
    pathHint: 'Finance → Manage Rate',
    related: ['rate-plans', 'dynamic-pricing-overview', 'reservation'],
    overview: [
      ...paragraphs(
        'Manage Rate is where you type or update the sell rate for each date, room type, and rate plan. It is the backbone of quotes at the desk and of pricing that distribution may follow depending on your channel setup.',
        'Empty or zero rates on future dates cause embarrassing zero quotes and blocked sales. Treat rate maintenance as seriously as room inventory.',
        'New bookings pick up current rates for the dates/plan selected. Existing bookings generally keep already posted amounts unless you amend the booking.',
        'Dynamic Pricing may recommend rates; publishing still needs human judgement. After recommendations, verify Manage Rate (and channel rates) before promising prices on WhatsApp.',
        'Weekend premiums, festival sheets, and corporate plan differences all live as grid values — document the strategy so night auditors do not “fix” a festival rate by accident.'
      ),
    ],
    beforeYouBegin: [
      finPerm,
      bullets([
        'Rate plans created under configuration.',
        'Room types created and sellable.',
        'Know floor rates and any contracted corporate prices.',
      ]),
    ],
    steps: [
      steps([
        { text: 'Open Finance → Manage Rate.' },
        {
          text: 'Select the rate plan you are maintaining.',
        },
        {
          text: 'Navigate to the date range (weekends, festivals, next 30 days).',
        },
        {
          text: 'Edit cells for each room type carefully — watch for stray zeros.',
        },
        { text: 'Save the grid.' },
        {
          text: 'Spot-check by creating a test quote/reservation on a demo flow or verifying an existing quote path.',
        },
        {
          text: 'Brief front office when festival or sudden changes go live.',
        },
      ]),
    ],
    screens: [
      bullets([
        'Rate plan selector',
        'Date navigation',
        'Room type rows / rate cells',
        'Save action',
      ]),
      fieldTable([
        ['Rate plan', 'Yes', 'Which tariff ladder you are editing'],
        ['Date / range', 'Yes', 'Nights affected'],
        ['Room type rate cell', 'Yes', 'Sell rate for that night'],
      ]),
    ],
    workflow: [
      workflow('Weekend premium', [
        'Select Fri–Sun',
        'Increase rates by plan',
        'Save',
        'Inform FO',
        'Spot-check quote',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Quotes and new reservations read rates from this grid (and related rate plan setup). Historical folios do not silently rewrite when you change tomorrow’s BAR.',
        'Hourly hotels still maintain rates per their plan structure — booking mode may be hourly, but price maintenance remains on the rate tools.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'With Dynamic Pricing',
        'Recommendations may suggest rates — publish carefully, then verify Manage Rate / channel rates.'
      ),
      callout(
        'warning',
        'Zero cells',
        'Scan for zeros after bulk edits. A single zero night breaks multi-night quotes.'
      ),
      callout(
        'tip',
        'Floor rate',
        'Keep a written floor. Discounting below floor needs manager approval, not silent grid edits.'
      ),
    ],
    bestPractices: [
      bullets([
        'Maintain a documented floor rate per room type.',
        'Keep festival rate sheets archived with the date they applied.',
        'Update next 30–60 days before the sales week starts.',
        'Align inventory strategy and rate strategy on the same day.',
        'Use corporate plans for contracts instead of one-off desk discounts when possible.',
        'Two-person review before large festival bulk updates.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Leaving zero rates on future dates.',
        'Editing the wrong rate plan (BAR vs corporate).',
        'Changing rates after a guest was already quoted without communication.',
        'Assuming OTA prices updated without checking distribution.',
        'Copy-pasting last year’s festival rates without cost review.',
        'Training staff on live rates instead of a demo hotel.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Quote is zero',
            'Empty Manage Rate cell',
            'Fill rate for plan/type/date; re-quote',
          ],
          [
            'Wrong plan price',
            'Reservation on different plan',
            'Check booking rate plan; edit correct plan grid',
          ],
          [
            'Save failed',
            'Network / validation',
            'Retry; reduce range; refresh',
          ],
          [
            'OTA price differs',
            'Channel markup/mapping',
            'Review Distribution rate mapping',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Hourly hotels?',
          a: 'Room types may use hourly booking mode — still maintain rates per your plan structure on Manage Rate.',
        },
        {
          q: 'Does changing rates alter in-house folios?',
          a: 'Existing posted amounts generally stay unless you amend the booking. New stays pick up new rates.',
        },
        {
          q: 'Who should edit rates?',
          a: 'Revenue/FO manager. Limit broad edit rights.',
        },
        {
          q: 'Relation to Dynamic Pricing?',
          a: 'Dynamic Pricing can recommend; Manage Rate (and channel publish paths) is where sellable prices are maintained/verified.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'draft-invoice',
    title: 'Draft Invoice',
    moduleId: 'finance',
    summary:
      'Generate standalone draft invoice PDFs for operational or accounting needs.',
    keywords: [
      'draft invoice',
      'invoice pdf',
      'proforma',
      'tax invoice',
      'corporate billing',
    ],
    pathHint: 'Finance → Draft Invoice',
    related: ['invoice-setup', 'check-out', 'booking-detail', 'reports-gst'],
    overview: [
      ...paragraphs(
        'Draft Invoice produces invoice PDFs when you need a formal document outside or alongside the standard checkout invoice path — for example corporate billing drafts, proforma-style reviews, or reprint needs per your SOP.',
        'The editor supports stay dates, room line items (room type, rate plan, rates), discounts, and corporate billing details when required. Totals recalculate as nights and lines change.',
        'Invoice branding and legal identity come from Invoice Setup / Finance Setup (logo, GSTIN, etc.). Blank headers almost always mean setup was skipped.',
        'Generate drafts for review; issue finals only when charges are locked and your accounting controls allow. Multiple conflicting invoice numbers create GST pain later.',
        'Always verify guest legal name and GST details for corporate invoices before sending PDFs on email or WhatsApp.'
      ),
    ],
    beforeYouBegin: [
      finPerm,
      bullets([
        'Invoice Setup completed (logo, address, tax lines).',
        'Finance Setup GSTIN/PAN accurate.',
        'Booking charges reviewed if the invoice is booking-linked.',
      ]),
    ],
    steps: [
      steps([
        { text: 'Open Finance → Draft Invoice.' },
        {
          text: 'Enter or confirm guest / corporate billing fields as required.',
        },
        {
          text: 'Set stay dates and room line items (room types, rate plans, amounts).',
        },
        {
          text: 'Apply discount mode carefully (percentage vs fixed) if offering concessions.',
        },
        {
          text: 'Review computed totals against the folio expectation.',
        },
        {
          text: 'Generate/download the PDF and share only through approved channels.',
        },
      ]),
    ],
    screens: [
      bullets([
        'Guest / corporate billing sections',
        'Stay date controls',
        'Line item grid (room types, rates, nights)',
        'Discount controls',
        'Totals panel',
        'Generate / download PDF',
      ]),
      fieldTable([
        ['Guest / corporate fields', 'As required', 'Legal name and billing identity'],
        ['Check-in / Check-out', 'Yes', 'Drives nights and line recalculation'],
        ['Room type / rate plan lines', 'Yes', 'What is being billed'],
        ['Rate / amount', 'Yes', 'Line pricing'],
        ['Discount', 'Optional', 'Fixed or percentage style per editor'],
        ['Generate PDF', 'Yes', 'Output document for guest/accounts'],
      ]),
    ],
    workflow: [
      workflow('Corporate request', [
        'Guest asks invoice',
        'Verify GST details',
        'Align charges',
        'Generate draft/final',
        'Email via approved process',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Invoice content pulls hotel invoice setup plus booking/charge context where linked. Line amounts recalculate with nights and room counts according to the editor logic.',
        'GST registers in Reports depend on invoices generated through billing processes — drafts used only for review still need a controlled final issuance path per your accounts SOP.'
      ),
    ],
    tips: [
      callout(
        'note',
        'GST',
        'Ensure Finance Setup GSTIN is correct before issuing tax invoices.'
      ),
      callout(
        'warning',
        'Charges first',
        'Do not generate “final” invoices while POS or extra charges are still open.'
      ),
      callout(
        'tip',
        'Corporate fields',
        'Capture company legal name and GSTIN exactly as registration — not the trade nickname.'
      ),
    ],
    bestPractices: [
      bullets([
        'Match legal name on GST documents.',
        'Avoid manual conflicting invoice numbers outside system controls.',
        'Keep draft vs final discipline with accounts.',
        'Store sent PDFs with the booking reference.',
        'Train FO to refuse last-minute GSTIN changes after invoice issuance without CA guidance.',
        'Spot-check logo and address on the first invoice after any setup change.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Generating before charges are final.',
        'Wrong GSTIN on setup producing bad documents.',
        'Sending draft watermarks as if final (if your process marks drafts).',
        'Mismatch between folio total and invoice lines.',
        'Emailing invoices from personal mailboxes without BCC to accounts.',
        'Editing Invoice Setup mid-month without telling the CA.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Blank logo/tax',
            'Invoice/Finance Setup incomplete',
            'Complete Configuration setups',
          ],
          [
            'Totals look wrong',
            'Nights/rooms/discount mismatch',
            'Recheck dates and line rates',
          ],
          [
            'Rate not fetching',
            'Missing Manage Rate / plan',
            'Fill rates; reselect room type/plan',
          ],
          [
            'PDF failed',
            'Browser block / empty lines',
            'Allow downloads; ensure required lines exist',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Final vs draft?',
          a: 'Use draft for review; issue final per accounting controls when charges are locked.',
        },
        {
          q: 'Is checkout invoice different?',
          a: 'Checkout often generates the stay invoice in the booking flow. Draft Invoice covers standalone/editor needs alongside that path.',
        },
        {
          q: 'Will this file GST for me?',
          a: 'No. Use Reports → GST with your accountant for filing support data.',
        },
        {
          q: 'Can I add all room types at once?',
          a: 'The editor supports adding line items and bulk room-type helpers where provided — still verify each rate.',
        },
      ]),
    ],
  }),
];

export const reportsGuides = [
  buildGuide({
    id: 'reports-hub',
    title: 'Reports Hub',
    moduleId: 'reports',
    summary:
      'Export hub for bookings, restaurant, customers, inventory, payments, GST, and police log.',
    keywords: [
      'reports',
      'export',
      'gst',
      'police log',
      'download',
      'excel',
      'pdf',
    ],
    pathHint: 'Finance → Reports',
    related: [
      'reports-booking',
      'reports-gst',
      'reports-police',
      'reports-restaurant',
      'reports-payments',
    ],
    overview: [
      ...paragraphs(
        'Reports Hub is the central export centre for operational registers. Pick a report type, set filters, preview, and download Excel/PDF for accounts, compliance, and owners.',
        'Available report cards cover Bookings, Restaurant, Customers, Inventory (rooms by type/cleaning-oriented snapshot), Payments (hotel or restaurant), GST, and Police log.',
        'Date-scoped reports enforce a maximum From–To span (90 days inclusive). Split longer audits into multiple exports rather than forcing huge ranges.',
        'Many exports include personal data. Police log and customer registers are especially sensitive — share only with authorised officers or staff under need-to-know.',
        'Stock goods reports live under Stock → Reports, not this hub. Night packs are usually Bookings + Payments + Cash Counter close run at shift end.'
      ),
      table(
        ['Report', 'What you get'],
        [
          ['Bookings', 'Reservations & stays export'],
          ['Restaurant', 'POS orders & billing'],
          ['Customers', 'Guest registry'],
          ['Inventory', 'Rooms by type / cleaning status snapshot'],
          ['Payments', 'Hotel or restaurant payment lines'],
          ['GST', 'Tax invoice register'],
          ['Police log', 'Verified guest ID log'],
        ]
      ),
    ],
    beforeYouBegin: [
      reportPerm,
      bullets([
        'Agree standard date ranges with accounts (often month-to-date or calendar month).',
        'Know who may receive PII-bearing files.',
      ]),
    ],
    steps: [
      steps([
        { text: 'Open Finance → Reports.' },
        { text: 'Select a report card (Bookings, GST, Payments, etc.).' },
        {
          text: 'Set From/To dates when the report uses dates; keep within the allowed max span.',
        },
        {
          text: 'Apply any scope filters (for example hotel vs restaurant payments).',
        },
        {
          text: 'Preview columns/rows if available, then download Excel or PDF.',
        },
        {
          text: 'Store securely — guest PII may be included.',
        },
      ]),
    ],
    screens: [
      bullets([
        'Report type tiles/cards',
        'Date pickers (From / To)',
        'Scope / column controls where provided',
        'Preview table',
        'Excel / PDF download actions',
      ]),
      fieldTable([
        ['Report type', 'Yes', 'Which register to build'],
        ['From / To', 'When dated', 'Max 90 days inclusive per export'],
        ['Scope filters', 'As shown', 'e.g. payments hotel vs restaurant'],
        ['Format', 'Yes', 'Excel and/or PDF'],
      ]),
    ],
    workflow: [
      workflow('Month-end pack', [
        'Close month charges',
        'Export GST',
        'Export Payments',
        'Export Bookings',
        'Share with CA securely',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Reports read posted operational data. Timing depends on checkout, payment posting, and ID verification already captured in the PMS.',
        'Empty files usually mean wrong dates, wrong hotel context, or data never posted — not a silent accounting close.'
      ),
    ],
    tips: [
      callout(
        'warning',
        'PII',
        'Police and customer exports contain sensitive data — share only with authorised officers/staff.'
      ),
      callout(
        'tip',
        '90-day limit',
        'If you need a full quarter, run multiple exports and label files by period.'
      ),
      callout(
        'tip',
        'Night pack',
        'Bookings + Payments + Cash Counter PDF is a practical night close set.'
      ),
    ],
    bestPractices: [
      bullets([
        'Standardise date ranges (1st–month end) with accounts.',
        'Password-protect files when emailing outside the property.',
        'Name files with hotel + report + date range.',
        'Preview before download when column selection is available.',
        'Keep a restricted shared drive folder for compliance exports.',
        'Never post police logs to public WhatsApp groups.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Exporting wrong hotel context after switching properties.',
        'Selecting a huge date range and hitting the 90-day validation error.',
        'Using local timezone assumptions incorrectly around midnight shifts.',
        'Emailing unencrypted guest lists.',
        'Confusing Stock Reports with this hub.',
        'Downloading GST before month charges are final.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Empty file',
            'No data / wrong dates',
            'Widen dates; confirm postings exist',
          ],
          [
            'Denied / missing menu',
            'Missing view_reports / manage_finance',
            'Ask admin for access',
          ],
          [
            'Date validation error',
            'Range > 90 days',
            'Split into smaller ranges',
          ],
          [
            'Preview slow',
            'Large result set',
            'Narrow filters; export in slices',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Night audit report?',
          a: 'Use Bookings + Payments + Cash Counter close as your night pack; run at shift end.',
        },
        {
          q: 'Where is stock valuation?',
          a: 'Stock → Reports (Inventory Valuation / related stock registers).',
        },
        {
          q: 'Can I schedule reports?',
          a: 'Hub exports are on-demand. Use a daily/monthly checklist with accounts.',
        },
        {
          q: 'Excel or PDF?',
          a: 'Excel for pivoting/reconciliation; PDF for sign-off packs. Many reports support both.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'reports-booking',
    title: 'Bookings Report',
    moduleId: 'reports',
    summary: 'Export reservations and stays for analysis and audits.',
    keywords: ['bookings report', 'reservation export', 'stay export'],
    pathHint: 'Reports → Bookings',
    related: ['booking-list', 'reports-hub'],
    overview: [
      ...paragraphs(
        'Bookings report exports reservation and stay rows for the selected filters so sales, FO managers, and auditors can analyse pickup without copying the booking list by hand.',
        'Use it for weekly source analysis, cancellation loss reviews, and month-end stay audits. Include cancelled rows when you are studying lost business — exclude them when you want occupied-stay lists only.',
        'Date filters are constrained by the hub’s maximum span. For long histories, export in monthly slices and keep a consistent naming scheme.',
        'Channel/OTA source fields appear when captured on the booking. Direct vs OTA pivots are only as good as source data quality at creation time.'
      ),
    ],
    beforeYouBegin: [
      reportPerm,
      bullets([
        'Decide whether cancelled/no-show rows should be included for this export purpose.',
        'Confirm hotel context in the top bar.',
      ]),
    ],
    steps: [
      steps([
        { text: 'Open Reports → Bookings.' },
        {
          text: 'Set From/To dates within the allowed range.',
        },
        {
          text: 'Apply status or other filters available for your export purpose.',
        },
        {
          text: 'Preview, then download Excel/PDF.',
        },
        {
          text: 'Archive the raw file before pivoting so you can re-check later.',
        },
      ]),
    ],
    screens: [
      bullets(['Date filters', 'Status / related filters', 'Preview', 'Export actions']),
      fieldTable([
        ['From / To', 'Yes', 'Stay/reservation window for the export'],
        ['Status filters', 'Optional', 'Include/exclude cancelled etc.'],
        ['Export format', 'Yes', 'Excel/PDF as offered'],
      ]),
    ],
    workflow: [
      workflow('Weekly sales review', [
        'Export bookings',
        'Pivot by source',
        'Review cancellations',
        'Act on weak channels',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Rows mirror booking records in range according to the report’s date logic (arrival/stay/create — follow the filter labels on screen).',
        'Comparing two exports is meaningless if status filters differ — always note filters in the file name or a cover note.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Include cancelled',
        'Sometimes you need cancelled rows for loss analysis — set filters accordingly and label the file.'
      ),
      callout(
        'tip',
        'Split large months',
        'If the month is huge, export weekly slices for faster downloads.'
      ),
      callout(
        'note',
        'Source quality',
        'Train FO to pick the correct booking source at creation so pivots stay trustworthy.'
      ),
    ],
    bestPractices: [
      bullets([
        'Keep raw exports archived monthly.',
        'Standardise filter sets for recurring owner reports.',
        'Reconcile room nights to Inventory/Performance narratives.',
        'Protect files that include guest phones/emails.',
        'Review no-shows separately from cancellations when policy differs.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Comparing exports with different status filters.',
        'Using create-date thinking when the filter is stay-date (or reverse).',
        'Emailing guest-laden booking dumps to large groups.',
        'Forgetting daylight boundaries around night audit.',
        'Mixing two hotels’ files in one spreadsheet.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Too large / slow',
            'Wide range',
            'Split date range; narrow filters',
          ],
          [
            'Missing OTA rows',
            'Not synced / wrong dates',
            'Confirm channel bookings exist in Booking List',
          ],
          [
            'Counts disagree with dashboard',
            'Different status/date basis',
            'Align filters; read filter labels',
          ],
          [
            'Empty export',
            'No bookings in range',
            'Widen dates; verify hotel',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'OTA source fields?',
          a: 'When channel bookings sync, source metadata appears where captured on the booking.',
        },
        {
          q: 'Same as Booking List?',
          a: 'Booking List is interactive ops UI; this report is export-oriented for analysis.',
        },
        {
          q: 'Can I get only in-house?',
          a: 'Use status filters where available to narrow to the stay states you need.',
        },
        {
          q: 'Guest documents included?',
          a: 'Use Police log / customer exports for ID-focused compliance — bookings export is stay-oriented.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'reports-restaurant',
    title: 'Restaurant Report',
    moduleId: 'reports',
    summary: 'Export POS orders and billing for F&B accounting.',
    keywords: ['restaurant report', 'pos export', 'fb report', 'kot export'],
    pathHint: 'Reports → Restaurant',
    related: ['pos-orders', 'pos-dashboard', 'reports-payments'],
    overview: [
      ...paragraphs(
        'Restaurant report extracts POS order and billing lines for reconciliation with kitchen, cashiers, and accounts.',
        'Use it daily in high-volume restaurants and after banquets when order volume spikes. Pair with restaurant payment exports to close F&B cash and UPI.',
        'It is POS-oriented — it does not replace room rent / lodging booking exports. Room service charges that attach to folios still need FO checkout awareness.',
        'Complimentary, void, and unpaid patterns should be reviewed — not only paid totals — or leakage stays invisible.'
      ),
    ],
    beforeYouBegin: [
      reportPerm,
      bullets([
        'Confirm POS was used for the period (not only offline paper KOTs).',
        'Agree whether unpaid orders should be chased before export.',
      ]),
    ],
    steps: [
      steps([
        { text: 'Open Reports → Restaurant.' },
        { text: 'Set the date range for service dates you are closing.' },
        {
          text: 'Preview order/billing rows; note voids/comps if visible in columns.',
        },
        {
          text: 'Export and reconcile to POS Dashboard and restaurant payments.',
        },
        {
          text: 'File with F&B daily close notes.',
        },
      ]),
    ],
    screens: [
      bullets(['Date filters', 'Preview grid', 'Export Excel/PDF']),
      fieldTable([
        ['From / To', 'Yes', 'Service period to export'],
        ['Export format', 'Yes', 'Excel/PDF as offered'],
      ]),
    ],
    workflow: [
      workflow('F&B close', [
        'Settle open tables',
        'Export restaurant report',
        'Export restaurant payments',
        'Match totals',
        'File',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Rows come from POS order/payment documents posted in Roomnexa. Paper-only sales never appear.',
        'Timezone/date boundaries matter for midnight restaurants — align “business date” habits with how orders were posted.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Unpaid check',
        'Review unpaid/open orders before export when your POS workflow shows them.'
      ),
      callout(
        'tip',
        'Banquet days',
        'Export immediately after the event while managers remember comps and special rates.'
      ),
      callout(
        'note',
        'Room rent excluded',
        'This report is restaurant/POS oriented — lodging stays belong in Bookings/Payments hotel scope.'
      ),
    ],
    bestPractices: [
      bullets([
        'Daily export in high-volume restaurants.',
        'Reconcile comps/voids with written approvals.',
        'Match payment methods to cashier declarations.',
        'Keep banquet exports labelled with event name in the file name.',
        'Train cashiers not to leave tables open overnight without reason.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Forgetting complimentary/void review.',
        'Comparing to dashboard with different date boundaries.',
        'Including room rent expectations in F&B totals.',
        'Exporting before settling open tables.',
        'Sharing raw guest order notes externally without need.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Mismatch vs dashboard',
            'Timezone/date boundaries',
            'Align From/To with POS business day',
          ],
          [
            'Missing orders',
            'Never posted in POS',
            'Confirm orders exist in POS Orders list',
          ],
          [
            'Empty export',
            'Wrong dates / no POS use',
            'Widen range; verify module usage',
          ],
          [
            'Totals off after comps',
            'Comps not approved/posted cleanly',
            'Audit void/comp lines with supervisor',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Includes room rent?',
          a: 'No — restaurant/POS oriented.',
        },
        {
          q: 'Where are restaurant payments?',
          a: 'Also export Reports → Payments with restaurant scope, and use POS payment screens operationally.',
        },
        {
          q: 'Does it include KOT text?',
          a: 'Columns follow the hub export schema — use POS Orders for full operational detail when needed.',
        },
        {
          q: 'How often to run?',
          a: 'Daily for busy F&B; at least after every banquet.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'reports-gst',
    title: 'GST Report',
    moduleId: 'reports',
    summary: 'Tax invoice register for GST filing support.',
    keywords: ['gst', 'tax report', 'invoice register', 'gstr'],
    pathHint: 'Reports → GST',
    related: ['finance-setup', 'draft-invoice', 'reports-hub'],
    overview: [
      ...paragraphs(
        'GST report helps Indian properties compile a tax invoice register from Roomnexa billing data for accountant/GST portal processes.',
        'It does not file GSTR for you. It provides structured invoice data so your CA can prepare returns. Timing and amendments still follow tax rules and your controls.',
        'Correct Finance Setup GSTIN and disciplined invoice generation at checkout/billing are prerequisites. Wrong setup produces wrong registers.',
        'Export after month charges are reasonably frozen. If you amend invoices after filing, tell your CA — exports are point-in-time snapshots.'
      ),
    ],
    beforeYouBegin: [
      reportPerm,
      bullets([
        'Finance Setup GST configured correctly.',
        'Invoice Setup complete.',
        'Month charges reviewed; large open folios cleared where possible.',
      ]),
    ],
    steps: [
      steps([
        { text: 'Open Reports → GST.' },
        {
          text: 'Select the tax period From/To (within allowed span; split long periods).',
        },
        {
          text: 'Preview invoice register rows for obvious gaps (missing invoice numbers, blank GSTIN).',
        },
        {
          text: 'Export and hand securely to your accountant.',
        },
        {
          text: 'Archive the exact file used for filing discussions.',
        },
      ]),
    ],
    screens: [
      bullets(['Period date filters', 'Preview register', 'Export Excel/PDF']),
      fieldTable([
        ['From / To', 'Yes', 'Tax period slice (mind 90-day hub limit)'],
        ['Export', 'Yes', 'File for CA / working papers'],
      ]),
    ],
    workflow: [
      workflow('Monthly GST', [
        'Close month charges',
        'Export GST register',
        'CA prepares GSTR',
        'File on portal',
        'Archive working papers',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Pulls tax-relevant invoice data posted in the period. If checkout never generated invoices, rows will be missing regardless of occupancy.',
        'Amendments after export mean your filed numbers may diverge from a later re-export — coordinate with the CA.'
      ),
    ],
    tips: [
      callout(
        'important',
        'Amendments',
        'If you alter invoices after filing, inform your CA — exports are point-in-time.'
      ),
      callout(
        'tip',
        'Setup first',
        'Fix GSTIN on Finance Setup before the first live tax invoice of the month.'
      ),
      callout(
        'warning',
        'Not a filing bot',
        'Roomnexa provides data support; portal filing remains your compliance process.'
      ),
    ],
    bestPractices: [
      bullets([
        'Freeze invoice edits after month close where possible.',
        'Reconcile GST export to Finance Dashboard revenue narratives.',
        'Keep CA’s preferred column layout notes with each month’s file.',
        'Separate lodging and F&B discussions if tax treatment differs in your books.',
        'Train FO never to invent invoice numbers outside the system.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Wrong GSTIN on setup producing bad registers.',
        'Exporting before checkout invoices exist for the stays.',
        'Re-exporting after silent edits without telling the CA.',
        'Mixing properties’ GST data in one email thread.',
        'Assuming draft invoices alone completed tax issuance.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Missing invoices',
            'Not generated on checkout/billing',
            'Ensure invoices generated; backfill per SOP',
          ],
          [
            'Blank GSTIN fields',
            'Setup incomplete / guest GST missing',
            'Fix Finance Setup; capture corporate GST where required',
          ],
          [
            'Period too long',
            'Hub 90-day limit',
            'Split into two exports',
          ],
          [
            'Totals surprise CA',
            'Refunds/amendments',
            'Provide Payments + notes with the GST file',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Does this file GST for me?',
          a: 'No — it provides data for your accountant/GST portal process.',
        },
        {
          q: 'Draft Invoice enough?',
          a: 'Drafts help produce documents; GST register depends on invoicing data posted in period. Follow accounts SOP for finals.',
        },
        {
          q: 'State vs central tax columns?',
          a: 'Register layout follows Roomnexa GST export fields — your CA maps them into GSTR worksheets.',
        },
        {
          q: 'Restaurant GST?',
          a: 'Include F&B invoicing per how your property bills; also use Restaurant/Payments exports for operational recon.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'reports-police',
    title: 'Police Log Report',
    moduleId: 'reports',
    summary: 'Export verified guest ID log for police compliance.',
    keywords: [
      'police log',
      'guest id',
      'compliance',
      'C-form',
      'frro',
    ],
    pathHint: 'Reports → Police log',
    related: ['walk-in', 'check-in', 'customer-detail'],
    overview: [
      ...paragraphs(
        'Police Log exports verified guest identity information required by local compliance processes. Exact submission format varies by city/authority — Roomnexa provides the register export your property uses as source data.',
        'Quality depends entirely on front office discipline at walk-in and check-in: document type, ID number, and verification fields must be captured when the guest arrives.',
        'This file is highly sensitive. Treat it as need-to-know. Never share on public WhatsApp groups or leave printed copies at the desk overnight.',
        'Incomplete IDs today become compliance pain tomorrow. Build a daily habit of checking arrivals for missing documents before night close.'
      ),
      callout(
        'warning',
        'Sensitive',
        'Handle this export under strict need-to-know. Encrypt at rest and control printouts.'
      ),
    ],
    beforeYouBegin: [
      reportPerm,
      bullets([
        'FO trained on mandatory ID fields at check-in/walk-in.',
        'Know your local police submission channel and cut-off time.',
      ]),
    ],
    steps: [
      steps([
        {
          text: 'During the day, ensure IDs are verified on walk-in and check-in flows.',
        },
        { text: 'Open Reports → Police log.' },
        {
          text: 'Set the required period From/To.',
        },
        {
          text: 'Preview for blank ID fields; backfill on booking/guest before final export if needed.',
        },
        {
          text: 'Export and submit via your local authorised process.',
        },
        {
          text: 'Archive the submission copy securely.',
        },
      ]),
    ],
    screens: [
      bullets(['Date filters', 'Preview', 'Export actions']),
      fieldTable([
        ['From / To', 'Yes', 'Compliance period'],
        ['Export', 'Yes', 'Register file for submission/working copy'],
      ]),
    ],
    workflow: [
      workflow('Compliance submit', [
        'Verify IDs daily',
        'Export police log',
        'Submit to authority process',
        'Archive securely',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Depends on guest document verification fields captured on bookings/profiles. Guests without documents will produce blank or incomplete rows.',
        'Foreign guest rules (where applicable locally) still require FO to capture the right document types at arrival — the export cannot invent missing passport data.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Daily discipline',
        'Incomplete IDs today become compliance pain tomorrow — check arrivals before night close.'
      ),
      callout(
        'tip',
        'Backfill path',
        'Use booking/guest detail document editors to fix blanks, then re-export.'
      ),
      callout(
        'warning',
        'WhatsApp',
        'Never drop police exports into large staff groups.'
      ),
    ],
    bestPractices: [
      bullets([
        'Train FO on mandatory ID fields with examples.',
        'Encrypt files at rest; limit folder ACL.',
        'Keep a submission checklist with time and officer/reference.',
        'Spot-check random arrivals weekly for document photo quality.',
        'Align Customer Hub documents with booking-time IDs for repeat guests.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Sharing police exports on public WhatsApp groups.',
        'Exporting before fixing blank IDs.',
        'Leaving printed logs at the unprotected desk.',
        'Using wrong date cut-offs vs local authority rules.',
        'Assuming Customer Hub profile alone satisfies arrival verification.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Blank IDs',
            'Not captured at check-in',
            'Backfill documents on booking/guest; re-export',
          ],
          [
            'Missing guests',
            'Wrong dates / not checked in',
            'Align period with arrivals; verify booking statuses',
          ],
          [
            'Authority rejects format',
            'Local template differs',
            'Map Roomnexa columns into required template',
          ],
          [
            'Access denied',
            'Permission',
            'Ask admin for reports access',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Format requirements?',
          a: 'Follow your local police authority template; Roomnexa provides the register export as source data.',
        },
        {
          q: 'Is this automatic filing?',
          a: 'No — export and submit through your local process.',
        },
        {
          q: 'Which document types?',
          a: 'Whatever FO captures (Aadhaar, passport, etc. as configured). Train to the types your locality expects.',
        },
        {
          q: 'Walk-ins included?',
          a: 'Yes when ID verification fields were captured on those stays.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'reports-customers',
    title: 'Customers Report',
    moduleId: 'reports',
    summary: 'Export guest registry data for CRM and audits.',
    keywords: ['customers report', 'guest export', 'crm export'],
    pathHint: 'Reports → Customers',
    related: ['customer-list', 'customer-dashboard', 'ai-retention'],
    overview: [
      ...paragraphs(
        'Customers report exports guest directory fields for analysis, audits, or controlled migration into marketing tools under privacy policy.',
        'It reads Customer Hub guest records. Guests who only exist as embedded booking fields may not appear until a customer profile exists — create/link profiles when CRM matters.',
        'This is PII. Follow consent rules before marketing. Prefer Roomnexa retention tools over raw spam from exported sheets when possible.',
        'Customer List is the interactive UI; this report is export-oriented for managers and compliance archives.'
      ),
    ],
    beforeYouBegin: [
      reportPerm,
      bullets([
        'Confirm privacy/consent rules with management before marketing use.',
        'Deduplicate expectations: mobile is the practical key.',
      ]),
    ],
    steps: [
      steps([
        { text: 'Open Reports → Customers.' },
        {
          text: 'Apply filters available for your export purpose.',
        },
        {
          text: 'Preview for obvious duplicates or blank mobiles.',
        },
        {
          text: 'Export carefully and store under access control.',
        },
        {
          text: 'If using for CRM, deduplicate on mobile before campaigns.',
        },
      ]),
    ],
    screens: [
      bullets(['Filters', 'Preview', 'Export actions']),
      fieldTable([
        ['Filters', 'Optional', 'Narrow the guest set'],
        ['Export', 'Yes', 'PII-bearing registry file'],
      ]),
    ],
    workflow: [
      workflow('CRM extract', [
        'Export customers',
        'Deduplicate by mobile',
        'Respect consent',
        'Campaign via retention/comms tools',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Reads Customer Hub guest records for the hotel/org context of your login.',
        'Marketing sends still depend on Communications configuration (email/WhatsApp) and consent — an Excel file alone does not send offers.'
      ),
    ],
    tips: [
      callout(
        'note',
        'Consent',
        'Follow privacy/consent rules before marketing from exports.'
      ),
      callout(
        'tip',
        'Prefer Retention',
        'AI Retention / customer offers keep messaging inside configured channels with less spreadsheet chaos.'
      ),
      callout(
        'warning',
        'Encryption',
        'Do not email unencrypted guest lists to large distribution groups.'
      ),
    ],
    bestPractices: [
      bullets([
        'Prefer retention module over raw spam blasts.',
        'Deduplicate on mobile before any campaign.',
        'Limit columns shared externally to need-to-know.',
        'Audit who downloaded customer exports monthly.',
        'Keep CRM extracts time-stamped and deleted when stale per policy.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Emailing unencrypted guest lists.',
        'Assuming every walk-in automatically has a full customer row.',
        'Merging two hotels’ guest lists casually.',
        'Using outdated exports months later for offers.',
        'Ignoring duplicate profiles that annoy guests with double messages.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Few rows',
            'Profiles not created',
            'Ensure customer records created/linked from stays',
          ],
          [
            'Duplicates',
            'Multiple profiles per mobile',
            'Search Customer List; follow merge SOP',
          ],
          [
            'Missing emails',
            'Never captured',
            'Capture at booking/add customer going forward',
          ],
          [
            'Access denied',
            'Permission',
            'Ask admin for reports access',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'vs Customer List?',
          a: 'List is interactive UI; report is export-oriented.',
        },
        {
          q: 'Good for WhatsApp campaigns?',
          a: 'Better to use AI Retention / configured customer offer communications with consent.',
        },
        {
          q: 'Includes documents?',
          a: 'Export fields follow the hub schema; document images are managed in Customer Detail / booking flows.',
        },
        {
          q: 'Org-wide guests?',
          a: 'Visibility depends on deployment scoping — confirm hotel context before exporting.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'reports-inventory',
    title: 'Inventory Report (Rooms)',
    moduleId: 'reports',
    summary: 'Export rooms by type and cleaning status snapshots.',
    keywords: [
      'inventory report',
      'room status export',
      'housekeeping export',
    ],
    pathHint: 'Reports → Inventory',
    related: [
      'housekeeping-room-status',
      'room-inventory-overview',
      'manage-inventory',
    ],
    overview: [
      ...paragraphs(
        'Inventory report in the Reports Hub helps managers snapshot room type distribution and cleaning-related status for operations meetings.',
        'It is a point-in-time ops snapshot — not a substitute for the live Housekeeping Room Status board when assigning cleaners right now.',
        'Do not confuse this with Stock → Reports (consumable goods) or with Manage Inventory (sellable availability grid). Names overlap in hotels; purpose differs.',
        'Export after the morning HK wave when you want a record of how the house looked for the briefing pack.'
      ),
    ],
    beforeYouBegin: [
      reportPerm,
      bullets([
        'Know whether you need live HK board (operations) or a file snapshot (meeting record).',
      ]),
    ],
    steps: [
      steps([
        { text: 'Open Reports → Inventory.' },
        { text: 'Set filters as offered for room types/status.' },
        {
          text: 'Export for HK/FO briefing or archive.',
        },
        {
          text: 'For live assignments, switch back to Housekeeping Room Status.',
        },
      ]),
    ],
    screens: [
      bullets(['Filters', 'Preview', 'Export actions']),
      fieldTable([
        ['Filters', 'Optional', 'Narrow room type / status view'],
        ['Export', 'Yes', 'Snapshot file for meetings'],
      ]),
    ],
    workflow: [
      workflow('Ops meeting', [
        'Morning HK wave',
        'Export inventory snapshot',
        'Align HK priorities with FO',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Snapshot of room/inventory related fields at export time. Status can change minutes later as HK completes jobs.',
        'Sellable date inventory strategy remains under Room Inventory / Manage Inventory — this report does not replace allotment edits.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Pair with HK board',
        'Live board is fresher for assignment; report is for records.'
      ),
      callout(
        'note',
        'Not Stock goods',
        'Consumables valuation is under Stock → Reports.'
      ),
      callout(
        'tip',
        'After morning wave',
        'Export once dirty checkout rooms are marked so the snapshot matches reality.'
      ),
    ],
    bestPractices: [
      bullets([
        'Export after morning HK wave for briefing packs.',
        'Label files with date/time — snapshots go stale quickly.',
        'Use live HK board for assignments, not yesterday’s Excel.',
        'Escalate OOO rooms from the snapshot into maintenance follow-up.',
        'Keep FO and HK looking at the same morning file in stand-up.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Using stale export for live assignments.',
        'Confusing with Stock goods reports.',
        'Confusing with Manage Inventory allotments.',
        'Ignoring OOO rows in the snapshot.',
        'Emailing room status with guest names unnecessarily.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Mismatch vs HK board',
            'Stale export / statuses moved',
            'Re-export; refresh HK statuses',
          ],
          [
            'Empty file',
            'Rooms not configured',
            'Confirm room setup in Configuration',
          ],
          [
            'Wrong module expectations',
            'Wanted stock or allotments',
            'Use Stock Reports or Manage Inventory',
          ],
          [
            'Access denied',
            'Permission',
            'Ask admin for reports access',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Stock goods report?',
          a: 'Use Stock → Reports for consumables.',
        },
        {
          q: 'Can I change allotments here?',
          a: 'No — use Manage Inventory for sellable availability edits.',
        },
        {
          q: 'Best time to export?',
          a: 'After morning housekeeping wave for ops meetings.',
        },
        {
          q: 'Includes guest names?',
          a: 'Schema is room/status oriented; use booking reports for guest-level stays.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'reports-payments',
    title: 'Payments Report',
    moduleId: 'reports',
    summary:
      'Export hotel or restaurant payment lines for reconciliation.',
    keywords: [
      'payments report',
      'settlement export',
      'upi recon',
      'cashier report',
    ],
    pathHint: 'Reports → Payments',
    related: ['payments', 'cash-counter', 'reports-restaurant'],
    overview: [
      ...paragraphs(
        'Payments report is the accountant-friendly register of tenders for hotel and/or restaurant contexts. Use it to reconcile cash, UPI, and card collections to bank settlements and Cash Counter closes.',
        'Choose the correct scope when prompted (hotel vs restaurant). Mixing scopes in one total without labelling causes false shortages.',
        'Refund lines appear when posted as payment events. Include them consciously when matching to bank statements.',
        'Run the same day for cash. Card/UPI may settle T+1 — label files with business date vs settlement date when talking to accounts.'
      ),
    ],
    beforeYouBegin: [
      reportPerm,
      bullets([
        'Close or review Cash Counter before daily cash recon.',
        'Know whether you are reconciling hotel FO, restaurant, or both (separately).',
      ]),
    ],
    steps: [
      steps([
        { text: 'Open Reports → Payments.' },
        {
          text: 'Choose hotel vs restaurant scope if prompted.',
        },
        {
          text: 'Set the business date range for the shift or day.',
        },
        {
          text: 'Export and pivot by method (cash/UPI/card).',
        },
        {
          text: 'Match cash to Cash Counter; match UPI/card to bank batches; sign off.',
        },
      ]),
    ],
    screens: [
      bullets(['Scope filters', 'Date filters', 'Preview', 'Export actions']),
      fieldTable([
        ['Scope', 'When shown', 'Hotel vs restaurant payment lines'],
        ['From / To', 'Yes', 'Business period to reconcile'],
        ['Export', 'Yes', 'Register for accounts/cashier sign-off'],
      ]),
    ],
    workflow: [
      workflow('Daily recon', [
        'Close cash',
        'Export payments',
        'Pivot by method',
        'Match UPI/card',
        'Sign off',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Lines come from payment documents posted in systems of record (booking payments, POS payments). Unposted WhatsApp “payments” do not appear.',
        'Finance → Payments is the interactive list; this report is the export for reconciliation packs.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Method pivot',
        'Pivot by method to spot missing card batches quickly.'
      ),
      callout(
        'tip',
        'Same-day cash',
        'Reconcile cash the same day — memory fades overnight.'
      ),
      callout(
        'note',
        'Refunds',
        'Refund lines appear when posted as payment events — do not ignore them in bank matching.'
      ),
    ],
    bestPractices: [
      bullets([
        'Reconcile cash same day.',
        'Keep hotel and restaurant scopes labelled in file names.',
        'Attach Cash Counter PDF with the payments export for night pack.',
        'Investigate duplicate payment amounts immediately.',
        'Train cashiers to store UTR in payment notes for faster matching.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Mixing scopes in one total without labelling.',
        'Matching settlement date to posting date blindly for UPI/card.',
        'Forgetting refunds in the bank match.',
        'Using Finance Dashboard alone without the line register.',
        'Emailing payment registers with full guest PII to broad lists.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Missing UPI',
            'Never posted on booking/POS',
            'Find folio/order; post if payment verified',
          ],
          [
            'Cash short vs counter',
            'Withdrawals / wrong scope',
            'Check Cash Counter ledger + scope filter',
          ],
          [
            'Duplicates',
            'Double post',
            'Trace timestamps; reverse per SOP',
          ],
          [
            'Empty export',
            'Wrong dates/scope',
            'Adjust filters; confirm payments exist in UI',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Includes refunds?',
          a: 'Refund lines appear when posted as payment events.',
        },
        {
          q: 'Hotel and restaurant together?',
          a: 'Select scope intentionally. Prefer separate exports when reconciling different cashiers.',
        },
        {
          q: 'vs Finance → Payments screen?',
          a: 'Screen is interactive ops; report is export for accounts recon.',
        },
        {
          q: 'Card batch missing?',
          a: 'Confirm posts exist, then match to bank settlement timing (often T+1).',
        },
      ]),
    ],
  }),
];
